import type { CodeContent } from '@/types/github';
import { createTwoFilesPatch } from 'diff';

import { languageMap } from '@/lib/language-utils/language-mapping';
import { binaryExtensions, imageExtensions as sharedImageExtensions } from '@/utils/file-utils';

export { binaryExtensions };
export { getSmartRenameDisplay, isPureRename } from '@/lib/diff/pr-diff-helpers';
export const imageExtensions = sharedImageExtensions;

export const isBinaryFile = (filename: string, content: string): boolean => {
  const extension = filename.split('.').pop()?.toLowerCase();
  const isBinaryExtension = extension ? binaryExtensions.has(extension) : false;
  const hasBinaryContent = content?.includes('\x00') ?? false;
  return isBinaryExtension || hasBinaryContent;
};

export const constructDiffText = (file: CodeContent): string | null => {
  const { filename, status, oldContent, newContent, previous_filename, patch } = file;
  const previousFilename = previous_filename || filename;

  // For renamed files with a patch, use it directly with the correct header
  if (status === 'renamed' && patch) {
    const renamedHeader = [
      `diff --git a/${previousFilename} b/${filename}`,
      `rename from ${previousFilename}`,
      `rename to ${filename}`,
      'similarity index 100%',
      `--- a/${previousFilename}`,
      `+++ b/${filename}`,
    ].join('\n');

    const cleanPatch = patch.replace(/^diff.*\n(?:.*\n)*?(?=@@)/, '');
    return `${renamedHeader}\n${cleanPatch}`;
  }

  const safeOldContent = oldContent ?? '';
  const safeNewContent = newContent ?? '';

  // When GitHub provides a patch, prefer it over extension-based binary detection.
  if (patch) {
    // Create a properly formatted git diff with all required parts
    return [
      `diff --git a/${filename} b/${filename}`,
      `index 0000000..0000000 100644`, // Dummy index line
      status === 'added' ? '--- /dev/null' : `--- a/${filename}`,
      status === 'removed' ? '+++ /dev/null' : `+++ b/${filename}`,
      patch,
    ].join('\n');
  }

  if (isBinaryFile(filename, safeOldContent) || isBinaryFile(filename, safeNewContent)) {
    return null;
  }

  // For other cases, use createTwoFilesPatch
  let diffOutput;

  if (status === 'modified') {
    diffOutput = createTwoFilesPatch(
      `a/${filename}`, // Note the a/ prefix
      `b/${filename}`, // Note the b/ prefix
      safeOldContent,
      safeNewContent,
      '',
      '',
      {
        context: 3, // To match github's diff context
      },
    );
  } else if (status === 'added') {
    diffOutput = createTwoFilesPatch('/dev/null', `b/${filename}`, '', safeNewContent, '', '', {
      context: 3,
    });
  } else if (status === 'removed') {
    diffOutput = createTwoFilesPatch(`a/${filename}`, '/dev/null', safeOldContent, '', '', '', {
      context: 3,
    });
  } else {
    return null;
  }

  // Format the output properly for parseDiff
  // Some diff libraries include "diff --git" but others don't
  if (!diffOutput.startsWith('diff --git')) {
    return `diff --git a/${filename} b/${filename}\n${diffOutput}`;
  }

  return diffOutput;
};

export const getLanguageFromFilename = async (filename: string): Promise<string> => {
  const extension = filename.split('.').pop()?.toLowerCase();
  const language = extension && languageMap[extension] ? languageMap[extension] : 'typescript';

  try {
    const refractor = (await import('refractor/core')).default;
    // Only import the language module if we haven't already
    if (!refractor.registered(language)) {
      console.log(`[PR-DIFF-HELPERS] Refractor loading language ${language}`);
      const lang = await import(`refractor/lang/${language}.js`);
      refractor.register(lang.default);
    }
    return language;
  } catch (error) {
    console.error(`Failed to load language ${language}:`, error);
    return 'typescript'; // Default to typescript even if loading fails
  }
};
